import {
  assoc, tryCatch, compose, always, head, omit,
} from 'ramda';
import to from 'await-to-js';
import { networkError, SESSION_MIDDLEWARE_CLEAR } from '@twnel/web-components';
import { parseApiCompany, allAgentFields } from '@twnel/utils-js/web';
import { UPDATE_AWS } from '../constants';
import { logException, isSessionCompany, goToLogin } from '../util';
import { updateAgent, fetchCompanyAgents, updateAgentPhoto } from './agents';
import { updateCompany, setSelectedCompany } from './companies';

const clearData = () => ({ type: SESSION_MIDDLEWARE_CLEAR });

const cleanAWS = (data) => {
  const { key, secret } = data;
  if (key && secret) {
    return { key, secret };
  }
  const { Key, Secret } = data;
  if (Key && Secret) {
    return { key: Key, secret: Secret };
  }
  logException('cleanAWS: Missing AWS credentials in data');
  throw networkError(500);
};

const processLogin = (data) => async (dispatch, getState, getContext) => {
  const { AWS, agents, companies } = data;
  if (!AWS || !agents || !companies) {
    logException('processLogin: Unexpected session data');
    throw networkError(500); // Unexpected server response
  }

  const processCompany = (() => {
    const agentData = agents.reduce((result, { company, phoneNumber, token } = {}) => (
      company && phoneNumber ? assoc(company, { phoneNumber, token }, result) : result
    ), {});
    return (company) => ({
      ...company,
      userAgent: agentData[company.id].phoneNumber,
      token: agentData[company.id].token,
      lastUpdate: Date.now(),
    });
  })();

  const filteredCompanies = companies
    .map(tryCatch(compose(processCompany, parseApiCompany), always({})))
    .filter(isSessionCompany);
  if (filteredCompanies.length < 1) {
    logException('processLogin: Unable to parse session data, no user-companies found');
    throw networkError(400); // Related companies not found
  }

  const { api } = getContext();
  const selectedCompany = head(filteredCompanies);
  const userAgent = await fetchCompanyAgents(api, selectedCompany, {
    agentId: selectedCompany.userAgent,
    pickFields: allAgentFields,
  });

  const aws = cleanAWS(AWS);
  dispatch(clearData());
  dispatch({ type: UPDATE_AWS, payload: aws });
  filteredCompanies.forEach(compose(dispatch, updateCompany));
  dispatch(updateAgent(selectedCompany, userAgent));
  dispatch(setSelectedCompany(selectedCompany.id));
};

// Http rejects with the numeric status code; anything else is treated as a server failure
const statusOf = (error) => (typeof error === 'number' ? error : error?.status);

const loginError = (error) => networkError(statusOf(error) < 500
  ? 400 // Invalid credentials or user not found
  : 500); // Network/server error

/**
 * Without a password, asks the api to send a one time code to the phone or email. With one,
 * logs in with username and password and loads the session into the store.
 * @param {string} userId Phone number, email or username
 * @param {string} [password]
 * @returns {import('../types').Thunk<Promise<any>>}
 */
export const companyLogin = (userId, password) => async (dispatch, getState, getContext) => {
  const { api } = getContext();
  const [error, data] = await to(api.companies.login(userId, password));
  if (!data) {
    throw loginError(error);
  }
  if (password) {
    return dispatch(processLogin(data));
  }
  return data;
};

/**
 * Verifies the one time code and loads the session into the store.
 * @param {string} userId Phone number or email the code was sent to
 * @param {string} otp
 * @returns {import('../types').Thunk<Promise<void>>}
 */
export const verifyLogin = (userId, otp) => async (dispatch, getState, getContext) => {
  const { api } = getContext();
  const [error, data] = await to(api.companies.verifyLogin(userId, otp));
  if (!data) {
    throw loginError(error);
  }
  return dispatch(processLogin(data));
};

/**
 * Clears the session everywhere and navigates to the login page.
 * @param {import('../types').Environment} environment
 * @returns {(dispatch: any) => void}
 */
export const logout = (environment) => (dispatch) => {
  dispatch(clearData());
  goToLogin(environment, { logout: true });
};

/**
 * Registers a company with its first agent and loads the resulting session into the store.
 * @param {{ name: string, industry: string, logo?: Blob, agentName: string, email: string,
 *   phoneNumber: string, role: string, photo?: Blob, terrachat?: { landscaping: boolean } }} data
 * @returns {import('../types').Thunk<Promise<void>>}
 */
export const registerCompany = (data) => async (dispatch, getState, getContext) => {
  const { api } = getContext();
  const [registerError, result] = await to(api.companies.register(omit(['photo'], data)));
  if (registerError) {
    throw networkError(statusOf(registerError) === 409
      ? 409 // Agent email already registered
      : 500); // Network/server error
  }

  const { APIKey: apikey, companyId } = result;
  if (!apikey || !companyId) {
    logException('registerCompany: Missing apikey or companyId in request response');
    throw networkError(500);
  }

  let options;
  const { phoneNumber, photo, terrachat } = data;
  if (terrachat?.landscaping) {
    options = { terrachat };
  }

  const [validateError, rawCompany] = await to(
    api.companies.validateRegister(apikey, options),
  );
  if (validateError) {
    logException('registerCompany: Validation request failed');
    throw networkError(500);
  }

  const aws = cleanAWS(rawCompany.AWS);
  const company = compose(omit(['AWS']), assoc('apikey', apikey))(rawCompany);

  if (photo) {
    const [agentError] = await to(updateAgentPhoto(aws, api, company, { phoneNumber }, photo));
    if (agentError) logException('registerCompany: Failed to upload agent\'s photo', true);
  }

  return dispatch(processLogin({
    AWS: aws,
    companies: [company],
    agents: [{
      company: companyId,
      phoneNumber,
    }],
  }));
};
